import { ReactNode } from "react";
import { ApiError } from "@app/backendApi/apiDispatcher";
import { Modal } from "./Modal";

/**
 * The message out of an error body, which this API sends as `{"error": "..."}`.
 *
 * Shown instead of the raw JSON — "Failed to delete item" reads as a sentence where
 * `{"error":"Failed to delete item"}` reads as a leak.
 */
function serverMessage(body: string): string | undefined {
  try {
    const parsed: unknown = JSON.parse(body);
    if (parsed && typeof parsed === "object" && "error" in parsed) {
      const message = (parsed as { error: unknown }).error;
      if (typeof message === "string" && message) return message;
    }
  } catch {
    // Not JSON. Whatever it is, it is still better than nothing.
  }
  return body || undefined;
}

/**
 * What went wrong, in a sentence somebody can act on.
 *
 * A 401 means the session lapsed and signing in again fixes it; a 404 means somebody
 * else got there first. Anything else is reported as it arrived rather than guessed at.
 */
function explain(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return "Your session has expired. Sign in again and retry.";
    }
    if (error.status === 404) {
      return "That item is no longer there — it may have been deleted already.";
    }
    return serverMessage(error.body) ?? error.message;
  }
  if (error instanceof Error) {
    // A fetch that never reached the server fails this way, with a terse message.
    return `${error.message}. The server may be unreachable.`;
  }
  return String(error);
}

/** The modal element to push onto the mount stack. */
export function errorModal(attempting: string, error: unknown): ReactNode {
  return (
    <Modal title="Something went wrong">
      <div className="flex flex-col gap-2">
        <p className="text-sm text-ink">Could not {attempting}.</p>
        <p
          role="alert"
          className="rounded-md bg-surface-sunken px-3 py-2 text-micro text-ink-muted"
        >
          {explain(error)}
        </p>
      </div>
    </Modal>
  );
}
